import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthPageShell from '../components/AuthPageShell';
import FullScreenSpinner from '../components/FullScreenSpinner';
import { getNextAuthRoute } from '../utils/authFlow';

export default function Login() {
  const {
    isAuthenticated,
    isEmailVerified,
    isLoading,
    isMfaChallengeRequired,
    isMfaEnrolled,
    login,
    resetPassword,
  } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  const nextRoute = getNextAuthRoute({
    isAuthenticated,
    isEmailVerified,
    isMfaChallengeRequired,
    isMfaEnrolled,
  });

  if (nextRoute !== '/login') {
    return <Navigate to={nextRoute} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    setIsSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to login');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email.trim()) {
      setError('Enter your email address first to reset your password.');
      setResetMessage('');
      return;
    }

    setError('');
    setResetMessage('');
    setIsSubmitting(true);

    try {
      await resetPassword(email);
      setResetMessage('Password reset email sent. Check your inbox.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send password reset email');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageShell
      title="Login to Dashboard"
      description="Sign in with your Firebase-managed admin account."
    >
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="email"
              className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white transition-colors duration-200"
              required
            />
            {error && (
              <div className="mt-2 text-red-600 dark:text-red-400 text-sm">{error}</div>
            )}
            {resetMessage && (
              <div className="mt-2 text-green-600 dark:text-green-400 text-sm">{resetMessage}</div>
            )}
          </div>
          <div className="mb-6">
            <label
              htmlFor="password"
              className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white transition-colors duration-200"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Please wait...' : 'Login'}
          </button>
        </form>
        <button
          type="button"
          onClick={handleResetPassword}
          className="mt-4 w-full text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-70 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          Forgot password?
        </button>
    </AuthPageShell>
  );
}
